import { useRef, useCallback } from 'react';
import type { LocationLink, LinkRegion } from '@roll1/shared';
import type { CanvasEngine } from './useCanvasEngine';
import {
  pointInRect, pointInCircle, pointInPolygon,
  hitTestRegion, getRegionCenter, getRegionWidth, truncateLabel,
} from '../utils/geometry';

export interface UseLocationLinksReturn {
  links: React.MutableRefObject<LocationLink[]>;
  hoveredLinkId: React.MutableRefObject<string | null>;

  getLinkAtWorld: (wx: number, wy: number) => LocationLink | null;
  setLinks: (newLinks: LocationLink[]) => void;
  renderLocationLinks: () => void;

  isLinkVisible: (link: LocationLink) => boolean;
}

export interface UseLocationLinksOptions {
  engine: CanvasEngine;
  isGM: boolean;
  /** All known page IDs — used to detect broken links */
  pageIds: string[];
}

/* ---- Style colors ---- */
const STYLE_COLORS: Record<string, string> = {
  portal: '99, 102, 241',   // indigo
  door: '234, 179, 8',      // amber
  stairs: '34, 197, 94',    // green
  area: '168, 85, 247',     // purple
};
const DEFAULT_COLOR = '148, 163, 184'; // slate

/* ---- Hook ---- */

export function useLocationLinks(options: UseLocationLinksOptions): UseLocationLinksReturn {
  const { engine, isGM, pageIds } = options;
  const links = useRef<LocationLink[]>([]);
  const hoveredLinkId = useRef<string | null>(null);

  const setLinks = useCallback((newLinks: LocationLink[]) => {
    links.current = newLinks;
    engine.markAllDirty();
  }, [engine]);

  /** Hit detection: iterate from end (topmost first) */
  const getLinkAtWorld = useCallback((wx: number, wy: number): LocationLink | null => {
    const arr = links.current;
    for (let i = arr.length - 1; i >= 0; i--) {
      const link = arr[i];
      if (!link.region || !('type' in link.region)) continue;
      if (hitTestRegion(wx, wy, link.region)) {
        return link;
      }
    }
    return null;
  }, []);

  const isLinkVisible = useCallback((link: LocationLink): boolean => {
    return link.visibility !== 'gm';
  }, []);

  /** Render all location links on the UI canvas layer */
  const renderLocationLinks = useCallback(() => {
    const canvas = engine.refs.current.ui;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const cam = engine.cameraRef.current;
    const arr = links.current;

    for (const link of arr) {
      const hidden = link.visibility === 'gm';
      const isHovered = hoveredLinkId.current === link.id;
      const isBroken = !pageIds.includes(link.targetPageId);

      // Players only see visible (non-hidden) links
      if (!isGM && hidden) continue;

      const rgb = STYLE_COLORS[link.style ?? ''] ?? DEFAULT_COLOR;

      ctx.save();

      // Determine opacity
      let fillAlpha: number;
      let strokeAlpha: number;
      if (isGM) {
        fillAlpha = hidden ? 0.12 : isHovered ? 0.45 : 0.25;
        strokeAlpha = hidden ? 0.3 : 0.7;
      } else {
        // Player: visible zones show thin contour, hovered shows full
        fillAlpha = isHovered ? 0.35 : 0.05;
        strokeAlpha = isHovered ? 0.8 : 0.3;
      }

      if (isBroken) {
        fillAlpha *= 0.4;
        strokeAlpha *= 0.5;
      }

      // Draw shape
      const region = link.region;
      if (!region || !region.type) {
        if (import.meta.env.DEV) console.warn('[LocationLinks] malformed region', link.id, link.region);
        ctx.restore(); continue;
      }
      ctx.beginPath();

      switch (region.type) {
        case 'rect': {
          const sx = region.x * cam.zoom + cam.offsetX;
          const sy = region.y * cam.zoom + cam.offsetY;
          const sw = region.width * cam.zoom;
          const sh = region.height * cam.zoom;
          ctx.rect(sx, sy, sw, sh);
          break;
        }
        case 'circle': {
          const sx = region.cx * cam.zoom + cam.offsetX;
          const sy = region.cy * cam.zoom + cam.offsetY;
          const sr = region.radius * cam.zoom;
          ctx.arc(sx, sy, sr, 0, Math.PI * 2);
          break;
        }
        case 'polygon': {
          const pts = region.points;
          if (pts.length < 3) { ctx.restore(); continue; }
          ctx.moveTo(pts[0].x * cam.zoom + cam.offsetX, pts[0].y * cam.zoom + cam.offsetY);
          for (let i = 1; i < pts.length; i++) {
            ctx.lineTo(pts[i].x * cam.zoom + cam.offsetX, pts[i].y * cam.zoom + cam.offsetY);
          }
          ctx.closePath();
          break;
        }
      }

      // Fill
      ctx.fillStyle = `rgba(${rgb}, ${fillAlpha})`;
      ctx.fill();

      // Stroke
      ctx.strokeStyle = `rgba(${rgb}, ${strokeAlpha})`;
      ctx.lineWidth = isHovered ? 3 : hidden ? 1 : 2;
      if (hidden) ctx.setLineDash([4, 4]);
      if (isBroken) ctx.setLineDash([2, 6]);
      ctx.stroke();
      ctx.setLineDash([]);

      // Label
      if (link.label) {
        const center = getRegionCenter(region);
        const scx = center.x * cam.zoom + cam.offsetX;
        const scy = center.y * cam.zoom + cam.offsetY;
        const fontSize = Math.max(10, Math.round(12 * cam.zoom));
        ctx.font = `${fontSize}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = isBroken ? 'rgba(239, 68, 68, 0.8)' : `rgba(255, 255, 255, 0.9)`;
        const maxWidth = getRegionWidth(region) * cam.zoom * 0.9;
        const displayLabel = truncateLabel(ctx, link.label, maxWidth);
        ctx.fillText(displayLabel, scx, scy);
      }

      // Broken link indicator
      if (isBroken) {
        const center = getRegionCenter(region);
        const scx = center.x * cam.zoom + cam.offsetX;
        const scy = center.y * cam.zoom + cam.offsetY - Math.round(14 * cam.zoom);
        ctx.font = `${Math.max(12, Math.round(16 * cam.zoom))}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.fillStyle = 'rgba(239, 68, 68, 0.9)';
        ctx.fillText('⚠', scx, scy);
      }

      ctx.restore();
    }
  }, [engine, isGM, pageIds]);

  return {
    links,
    hoveredLinkId,
    getLinkAtWorld,
    setLinks,
    renderLocationLinks,
    isLinkVisible,
  };
}

// Geometry helpers (pointInRect, pointInCircle, pointInPolygon, hitTestRegion,
// getRegionCenter, getRegionWidth, truncateLabel) are imported from '../utils/geometry'
